// Notice obligations (Avisos to the UIF, filed through the SAT portal).
//
// An operation requires a notice when, WITHOUT VAT and regardless of the
// payment method, it reaches the notice threshold (1,605 UMA):
//   - individually, or
//   - accumulated: per client, over a rolling 6-month window, adding only the
//     operations that individually reach the identification threshold
//     (805 UMA). Smaller operations never accumulate.
//
// Design decisions (documented in docs/, to confirm with the client's legal team):
//   - Operations that already trigger an individual notice are not added to
//     the accumulation again, so one sale never produces two notices.
//   - The window is inclusive: an operation exactly 6 months before the
//     current one still counts (the more conservative reading).
//   - Once the accumulated sum crosses the threshold, the operations in the
//     window are covered by that notice and accumulation restarts.
import { ACCUMULATION_MONTHS } from "./constants";
import { addMonths, monthOf } from "./dates";
import { noticeDeadline } from "./deadlines";
import { roundCents } from "./money";
import type { Thresholds } from "./thresholds";
import type { IsoDate, IsoMonth, Operation } from "./types";

export type NoticeKind = "individual" | "accumulated";

/** A notice the company must file, derived from its operations. */
export interface NoticeObligation {
  /** Stable id, also used as the alert id: `notice-ind-<operationId>` or `notice-acc-<operationId>`. */
  id: string;
  kind: NoticeKind;
  clientId: string;
  /** Operation whose `noticeFiled` flag records that this notice was filed. */
  triggerOperationId: string;
  /** Every operation covered by the notice (just one for individual notices). */
  operationIds: string[];
  /** Date of the trigger operation; its month is the reporting month. */
  date: IsoDate;
  month: IsoMonth;
  /** Amount without VAT: the operation's own amount, or the accumulated sum. */
  amount: number;
  deadline: IsoDate;
  filed: boolean;
}

export type NoticeOperationInput = Pick<Operation, "id" | "clientId" | "date" | "amount" | "noticeFiled"> & {
  createdAt?: string;
};

/** Stable chronological order: date, then capture time, then id. */
const chronological = (a: NoticeOperationInput, b: NoticeOperationInput) =>
  a.date.localeCompare(b.date) || (a.createdAt ?? "").localeCompare(b.createdAt ?? "") || a.id.localeCompare(b.id);

function obligation(
  kind: NoticeKind,
  trigger: NoticeOperationInput,
  covered: readonly NoticeOperationInput[],
  amount: number,
): NoticeObligation {
  return {
    id: `${kind === "individual" ? "notice-ind" : "notice-acc"}-${trigger.id}`,
    kind,
    clientId: trigger.clientId,
    triggerOperationId: trigger.id,
    operationIds: covered.map((o) => o.id),
    date: trigger.date,
    month: monthOf(trigger.date),
    amount,
    deadline: noticeDeadline(trigger.date),
    filed: trigger.noticeFiled,
  };
}

export function computeNoticeObligations(
  operations: readonly NoticeOperationInput[],
  thresholds: Thresholds,
): NoticeObligation[] {
  const sorted = [...operations].sort(chronological);
  const obligations: NoticeObligation[] = [];

  // 1. Individual notices.
  for (const op of sorted) {
    if (op.amount >= thresholds.notice) obligations.push(obligation("individual", op, [op], op.amount));
  }

  // 2. Accumulated notices, per client.
  const accumulableByClient = new Map<string, NoticeOperationInput[]>();
  for (const op of sorted) {
    if (op.amount >= thresholds.identification && op.amount < thresholds.notice) {
      const list = accumulableByClient.get(op.clientId);
      if (list) list.push(op);
      else accumulableByClient.set(op.clientId, [op]);
    }
  }
  for (const clientOperations of accumulableByClient.values()) {
    let window: NoticeOperationInput[] = [];
    for (const op of clientOperations) {
      const windowStart = addMonths(op.date, -ACCUMULATION_MONTHS);
      window = [...window.filter((o) => o.date >= windowStart), op];
      const total = roundCents(window.reduce((sum, o) => sum + o.amount, 0));
      if (total >= thresholds.notice) {
        obligations.push(obligation("accumulated", op, window, total));
        window = [];
      }
    }
  }

  return obligations.sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id));
}

/** Index of the notices that cover each operation (used by the operations table). */
export function noticesByOperation(obligations: readonly NoticeObligation[]): Map<string, NoticeObligation[]> {
  const index = new Map<string, NoticeObligation[]>();
  for (const notice of obligations) {
    for (const operationId of notice.operationIds) {
      const list = index.get(operationId);
      if (list) list.push(notice);
      else index.set(operationId, [notice]);
    }
  }
  return index;
}
